import { ArrowForwardIos, Check } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import HomeIcon from '../../../components/icons/HomeIcon';
import { useEnv } from '../../../context/EnvProvider';
import { useUser } from '../../../context/UserProvider';
import paths from '../../../navigation/paths';

const steps = {
  CATALOG: 'Catalogue',
  CUSTOMIZATION: 'Personnalisation du monument',
  ACCESSORIES: 'Accessoires',
  PATTERNS: 'Motifs & Gravures',
  QUOTE: 'Devis',
} as const;

export type QuoteStep = keyof typeof steps;

const stepsEntries = Object.entries(steps);
type StepKeys =
  | 'CATALOG'
  | 'CUSTOMIZATION'
  | 'PATTERNS'
  | 'ACCESSORIES'
  | 'QUOTE';

const NavBar = ({
  currStep,
  setStep,
  handleSave,
}: {
  currStep: QuoteStep;
  setStep?: (step: StepKeys) => void;
  handleSave?: () => Promise<void>;
}) => {
  const { buckets } = useEnv();
  const { user } = useUser();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const handleGoHome = useCallback(async () => {
    if (!handleSave) navigate(`/${paths.home.root}`);
    else {
      setIsSaving(true);
      try {
        if (handleSave) await handleSave();
        navigate(`/${paths.home.root}`);
      } finally {
        setIsSaving(false);
      }
    }
  }, [handleSave, navigate]);

  const currStepIndex = stepsEntries
    .map(([key]) => key)
    .indexOf(currStep || '');

  return (
    <div className='flex justify-between items-center flex-[0_1_100px] gap-5 bg-white border-b border-[#EAECF0] w-full h-[100px] shadow-[0px_2px_6px_rgba(140,140,140,0.1)] z-[2]'>
      <div className='h-full flex items-center gap-6'>
        <button
          type='button'
          disabled={isSaving}
          onClick={handleGoHome}
          className='flex justify-center items-center bg-turquoise h-full gap-2.5 px-[30px] cursor-pointer text-white no-underline border-none'
        >
          {isSaving ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            <HomeIcon color='primary' />
          )}
          Accueil
        </button>
        <img src='/logo_paysage.svg' className='h-[60%]' alt='Logo' />
      </div>
      <div className='flex flex-1 items-center gap-4 overflow-x-auto h-full'>
        {stepsEntries.map(([key, value], index) => {
          return (
            <div className='flex items-center gap-4 font-bold' key={key}>
              {currStepIndex >= index ? (
                <div
                  className='flex items-center gap-2.5 font-semibold cursor-pointer'
                  onClick={() => {
                    if (setStep && key !== 'CATALOG') setStep(key as StepKeys);
                  }}
                >
                  <div className='w-10 h-10 rounded-full bg-turquoise flex items-center justify-center text-white flex-[0_0_40px] font-bold'>
                    {currStepIndex === index ? index + 1 : <Check />}
                  </div>
                  <span>{value}</span>
                </div>
              ) : (
                <div className='flex items-center gap-2.5 no-underline text-[#98A2B3] font-semibold'>
                  <div className='w-10 h-10 rounded-full bg-white text-[#98A2B3] border border-[#EAECF0] flex items-center justify-center flex-[0_0_40px] font-bold'>
                    {index + 1}
                  </div>
                  <span>{value}</span>
                </div>
              )}

              {index + 1 !== Object.keys(steps).length && (
                <ArrowForwardIos className='text-[#D0D5DD]' />
              )}
            </div>
          );
        })}
      </div>
      <img
        src={`${buckets?.BUCKET_PROFILE_PICTURES || ''}/${
          user?.profilePicture ?? 'default'
        }`}
        className='h-[60%] object-contain rounded-3xl mr-2.5'
        alt='Profile'
      />
    </div>
  );
};

export default NavBar;
